import type { AriaAttributes, FC, KeyboardEvent } from 'react';
import React, { useCallback, useRef } from 'react';

import { Item } from './components/Item/Item';
import { Container, Wrapper } from './Menu.styled';

export interface MenuProps extends Pick<AriaAttributes, 'aria-label'> {
  /**
   * Property used for testing purposes
   */
  dataCy?: string;
  children?: React.ReactNode;
}

const ValidKeyCodes = ['Home', 'End', 'ArrowUp', 'ArrowDown'];

const newIndex = (keycode: string, currentIndex: number, total: number): number => {
  switch (keycode) {
    case 'Home':
      return 0;
    case 'End':
      return total - 1;
    case 'ArrowDown':
      return currentIndex + 1 >= total ? 0 : currentIndex + 1;
    case 'ArrowUp':
      return currentIndex - 1 < 0 ? total - 1 : currentIndex - 1;
    default:
      return 0;
  }
};

const isValidKeyCode = (code: string) => {
  return ValidKeyCodes.includes(code);
};

const Menu: FC<MenuProps> & { Item: typeof Item } = ({
  children,
  'aria-label': ariaLabel,
  dataCy,
}) => {
  const childrenRefs = useRef<HTMLElement[]>([]);
  const filteredChildren = React.Children.map(children, child => {
    if (React.isValidElement(child)) {
      return child;
    }
    return null;
  });

  // This keyboard handler does keyboard accesibility
  const handleKeyDown = useCallback(
    ({ code }: KeyboardEvent) => {
      if (!isValidKeyCode(code)) {
        return;
      }

      // Find the focused children
      const selectedChildIndex = childrenRefs.current.findIndex(ref => {
        return ref.contains(document.activeElement);
      });

      // If none is selected, we will select the first one, otherwise calculate based on
      // keyboard event
      const nextIndex =
        selectedChildIndex === -1
          ? 0
          : newIndex(code, selectedChildIndex, React.Children.count(filteredChildren));

      const nextChild = React.Children.toArray(filteredChildren)[nextIndex];
      const childToFocus = childrenRefs.current[nextIndex]?.firstChild;

      // Focus the next child
      if (React.isValidElement(nextChild) && childToFocus instanceof HTMLElement) {
        childToFocus.focus();
      }
    },
    [filteredChildren],
  );

  return (
    <Container>
      <Wrapper
        data-cy={dataCy ? `${dataCy}Menu` : undefined}
        aria-label={ariaLabel}
        role="tablist"
        onKeyDown={handleKeyDown}
      >
        {React.Children.map(filteredChildren, (child, index) => {
          return (
            <div
              ref={(ref: HTMLDivElement) => {
                childrenRefs.current[index] = ref;
              }}
            >
              {child}
            </div>
          );
        })}
      </Wrapper>
    </Container>
  );
};

Menu.Item = Item;

export default Menu;
